import type { FilterField, FilterOption } from '@/components/common/AdvancedFilter';
import type { CountByUser } from '@/types/countsBySubmitter.types';

export const FILTER_KEYS = {
  companyId: 'companyId',
  departmentId: 'departmentId',
  divisionId: 'divisionId',
  districtId: 'districtId',
  thanaId: 'thanaId',
} as const;

export const OPTION_DETAIL_SEPARATOR = ' · ';

export type OptionWithCount<T> = T & { count?: number | undefined };

export function parseNumberFilter(value: string | undefined): number | undefined {
  if (!value) return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

export function parseTextFilter(value: string | undefined): string | undefined {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
}

const MULTI_FILTER_SEPARATOR = ',';

export function splitMultiFilterValue(filterText: string | undefined): string[] {
  return filterText ? filterText.split(MULTI_FILTER_SEPARATOR) : [];
}

export function joinMultiFilterValues(values: readonly string[]): string {
  return values.join(MULTI_FILTER_SEPARATOR);
}

export function toNumberListFilterText(values: readonly number[] | undefined): string {
  return joinMultiFilterValues((values ?? []).map(String));
}

export function parseNumberListFilter(filterText: string | undefined): number[] | undefined {
  const numbers = splitMultiFilterValue(filterText).map(Number).filter(Number.isFinite);
  return numbers.length > 0 ? numbers : undefined;
}

export const ACTIVE_STATUS_FILTER_VALUE = { active: 'active', inactive: 'inactive' } as const;

export function toActiveStatusFilterText(isActive: boolean | undefined): string {
  if (isActive === undefined) return '';
  return isActive ? ACTIVE_STATUS_FILTER_VALUE.active : ACTIVE_STATUS_FILTER_VALUE.inactive;
}

export function parseActiveStatusFilter(filterText: string | undefined): boolean | undefined {
  if (filterText === ACTIVE_STATUS_FILTER_VALUE.active) return true;
  if (filterText === ACTIVE_STATUS_FILTER_VALUE.inactive) return false;
  return undefined;
}

export const BOOLEAN_FILTER_VALUE = { yes: 'true', no: 'false' } as const;

export function toBooleanFilterText(value: boolean | undefined): string {
  if (value === undefined) return '';
  return value ? BOOLEAN_FILTER_VALUE.yes : BOOLEAN_FILTER_VALUE.no;
}

export function parseBooleanFilter(filterText: string | undefined): boolean | undefined {
  if (filterText === BOOLEAN_FILTER_VALUE.yes) return true;
  if (filterText === BOOLEAN_FILTER_VALUE.no) return false;
  return undefined;
}

export function parseEnumFilter<T extends string>(
  value: string | undefined,
  allowed: readonly T[],
): T | undefined {
  return allowed.find((option) => option === value);
}

interface FilterOptionSource {
  id: number;
  name: string;
  count?: number | undefined;
}

export function toFilterOptions<T extends FilterOptionSource>(
  items: readonly T[] | undefined,
): FilterOption[] {
  return (
    items?.map((item) => ({ label: item.name, value: String(item.id), count: item.count })) ?? []
  );
}

export function withOptionCounts<T extends { id: number }>(
  options: T[] | undefined,
  counts: CountByUser[] | undefined,
): OptionWithCount<T>[] | undefined {
  if (!options || !counts) return options;

  const countByUserId = new Map(counts.map((item) => [item.userId, item.count]));
  return options.map((option) => ({ ...option, count: countByUserId.get(option.id) }));
}

export function getSelectFieldsWhenOptionsExist<T extends FilterOptionSource>(
  key: string,
  label: string,
  items: readonly T[] | undefined,
): FilterField[] {
  if (items?.length === 0) return [];
  return [{ key, label, type: 'select', options: toFilterOptions(items), searchable: true }];
}

export function toFilterText(value: string | number | undefined): string {
  return value === undefined ? '' : String(value);
}

export function countActiveFilters(filters: Record<string, string>): number {
  return Object.values(filters).filter(Boolean).length;
}

interface PeriodSearch {
  dateFrom?: string | undefined;
  dateTo?: string | undefined;
}

interface DefaultPeriod {
  dateFrom: string;
  dateTo: string;
}

export function toPeriodFilterValues(
  search: PeriodSearch,
  defaultPeriod: DefaultPeriod,
): Record<string, string> {
  return {
    dateFrom: search.dateFrom ?? defaultPeriod.dateFrom,
    dateTo: search.dateTo ?? defaultPeriod.dateTo,
  };
}

export function toPeriodSearch(values: Record<string, string>): PeriodSearch {
  return { dateFrom: values.dateFrom || undefined, dateTo: values.dateTo || undefined };
}

interface GeoSearch {
  divisionId?: number | undefined;
  districtId?: number | undefined;
  thanaId?: number | undefined;
}

export function toGeoFilterValues(search: GeoSearch): Record<string, string> {
  return {
    [FILTER_KEYS.divisionId]: toFilterText(search.divisionId),
    [FILTER_KEYS.districtId]: toFilterText(search.districtId),
    [FILTER_KEYS.thanaId]: toFilterText(search.thanaId),
  };
}

export function toGeoSearch(values: Record<string, string>): GeoSearch {
  return {
    divisionId: parseNumberFilter(values[FILTER_KEYS.divisionId]),
    districtId: parseNumberFilter(values[FILTER_KEYS.districtId]),
    thanaId: parseNumberFilter(values[FILTER_KEYS.thanaId]),
  };
}

export function fillEmptyFilterValues<TValues extends Record<string, string>>(
  values: TValues,
  defaultValues: Partial<Record<string, string>>,
  filterKeys: readonly string[],
): TValues {
  const filledDefaults = Object.fromEntries(
    filterKeys
      .filter((filterKey) => !values[filterKey] && defaultValues[filterKey])
      .map((filterKey) => [filterKey, defaultValues[filterKey]]),
  );
  return { ...values, ...filledDefaults };
}

export function areFilterValuesEqual(
  left: Record<string, string>,
  right: Record<string, string>,
): boolean {
  const filterKeys = new Set([...Object.keys(left), ...Object.keys(right)]);
  return [...filterKeys].every((filterKey) => (left[filterKey] ?? '') === (right[filterKey] ?? ''));
}

const DEPENDENT_FILTER_KEYS: Partial<Record<string, readonly string[]>> = {
  [FILTER_KEYS.companyId]: [FILTER_KEYS.departmentId],
  [FILTER_KEYS.divisionId]: [FILTER_KEYS.districtId, FILTER_KEYS.thanaId],
  [FILTER_KEYS.districtId]: [FILTER_KEYS.thanaId],
};

export function clearDependentFilterValues<TValues extends Record<string, string>>(
  previous: TValues,
  next: TValues,
): TValues {
  const changedParentKeys = Object.keys(DEPENDENT_FILTER_KEYS).filter(
    (parentKey) => parentKey in next && next[parentKey] !== previous[parentKey],
  );
  const clearedChildValues = Object.fromEntries(
    changedParentKeys
      .flatMap((parentKey) => DEPENDENT_FILTER_KEYS[parentKey] ?? [])
      .filter((childKey) => childKey in next && next[childKey] === previous[childKey])
      .map((childKey) => [childKey, '']),
  );
  return { ...next, ...clearedChildValues };
}
